import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import styles from "./ComingSoon.module.css";

import { useNavigate } from "react-router-dom";

/* =========================================================
   COUNTDOWN DURATION (in seconds from now)
   Change this if you want a real target date
========================================================= */

const COUNTDOWN_SECONDS =
  12 * 24 * 60 * 60 + // 12 days
  8 * 60 * 60 +       // 8 hours
  24 * 60 +           // 24 minutes
  36;                 // 36 seconds

/* =========================================================
   HELPER: format number with leading zero
========================================================= */

const pad = (value) => String(value).padStart(2, "0");

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function ComingSoon({ onBack }) {
  const navigate = useNavigate();

  /* ------- Countdown target timestamp (fixed on mount) ------- */
  const targetTimeRef = React.useRef(
    Date.now() + COUNTDOWN_SECONDS * 1000
  );

  /* ------- Time state ------- */
  const [timeLeft, setTimeLeft] = useState(() => {
    const diff = Math.max(
      0,
      Math.floor(
        (targetTimeRef.current - Date.now()) / 1000
      )
    );

    return diff;
  });

  /* ------- Countdown effect ------- */
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(
        0,
        Math.floor(
          (targetTimeRef.current - Date.now()) / 1000
        )
      );

      setTimeLeft(diff);

      if (diff <= 0) {
        clearInterval(timer);
      }
    };

    const timer = setInterval(tick, 1000);

    /* Initial tick */
    tick();

    return () => clearInterval(timer);
  }, []);



  /* ------- Back handler ------- */
  const handleBack = useCallback(() => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }

    navigate(-1);
  }, [onBack, navigate]);

  /* ------- Render ------- */
  return (
    <div className={styles.screenWrapper}>
      {/* ============ ANIMATED BACKGROUND ============ */}

      <div className={styles.backgroundGlow}></div>
      <div className={styles.glowOne}></div>
      <div className={styles.glowTwo}></div>
      <div className={styles.glowThree}></div>

      {/* ============ DEVICE FRAME ============ */}

      <div className={styles.deviceFrame}>
        {/* ============ TOP GLASS HEADER ============ */}

        <header className={styles.appBar}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={handleBack}
            aria-label="Go Back"
          >
            <svg
              width="20"
              height="16"
              viewBox="0 0 24 16"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="2.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 8H5" />
              <polyline points="12 1 5 8 12 15" />
            </svg>
          </button>

          <div className={styles.logo}>
            <span className={styles.logoBro}>Bro</span>
            <span className={styles.logoBizee}>Bizee</span>
          </div>

          <div className={styles.headerDot}></div>
        </header>

        {/* ============ MAIN SCROLL AREA ============ */}

        <main className={styles.scrollContainer}>
          {/* Floating Sparkles */}
          <span
            className={`${styles.sparkle} ${styles.sparkleOne}`}
            aria-hidden="true"
          >
            ✦
          </span>

          <span
            className={`${styles.sparkle} ${styles.sparkleTwo}`}
            aria-hidden="true"
          >
            ✧
          </span>

          <span
            className={`${styles.sparkle} ${styles.sparkleThree}`}
            aria-hidden="true"
          >
            ✦
          </span>

          {/* ============ HERO SECTION ============ */}

          <section className={styles.heroSection}>
            {/* Animated Icon */}
            <div className={styles.iconArea}>
              <div className={styles.outerRing}></div>

              <div className={styles.iconCircle}>
                <div
                  className={styles.rocket}
                  role="img"
                  aria-label="Rocket launching"
                >
                  🚀
                </div>
              </div>

              <div
                className={`${styles.orbit} ${styles.orbitOne}`}
              ></div>

              <div
                className={`${styles.orbit} ${styles.orbitTwo}`}
              ></div>
            </div>

            {/* Badge */}
            <div className={styles.comingBadge}>
              <span
                className={styles.pulseDot}
                aria-hidden="true"
              ></span>
              SOMETHING NEW IS COMING
            </div>

            {/* Main Heading */}
            <h1 className={styles.mainTitle}>
              Coming
              <span className={styles.titleHighlight}>
                {" "}
                Soon
              </span>
              <span className={styles.titleDot}>.</span>
            </h1>

            {/* Description */}
            <p className={styles.description}>
              We are building something amazing for you.
              <br />
              Get ready for the next big BroBizee experience.
            </p>

            

            {/* Glass Information Card */}
            <div className={styles.infoCard}>
              <div className={styles.infoIcon}>✨</div>

              <div className={styles.infoContent}>
                <h3>Stay Tuned</h3>
                <p>
                  Your next BroBizee feature is almost ready.
                  We can't wait to show you!
                </p>
              </div>
            </div>

            {/* Progress */}
            <div className={styles.progressSection}>
              <div className={styles.progressHeader}>
                <span>Building the experience</span>
                <strong>85%</strong>
              </div>

              <div className={styles.progressTrack}>
                <div className={styles.progressBar}>
                  <span></span>
                </div>
              </div>
            </div>

            {/* Bottom Message */}
            <div className={styles.bottomMessage}>
              <span>On Time.</span>
              <span>Online.</span>
              <span>On Demand.</span>
              <span
                className={styles.heart}
                aria-label="love"
              >
                ♡
              </span>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}